/* eslint-disable camelcase */
import { ParsedUrlQuery } from "querystring";
import React, { useEffect, useState } from "react";
import { GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import getConfig from "next/config";
import NodeCache from "node-cache";
import { useRouter } from "next/router";
import { v4 as uuidv4 } from "uuid";
import {
    Experiment,
    ExperimentProvider,
    getExperiments,
} from "@themuse/experiment-client";
import { SearchDropdownProps } from "@themuse/design-system";
import { initSnowplowTracker, triggerSnowplowPageViewEvent } from "@themuse/snowplow-js-client";
import { searchResultsSelector } from "selectors";
import { formatLog } from "utils/logging";
import { getParams } from "utils/searchResults";
import { userTrack } from "utils/tracking";
import { UserProvider, useUserState } from "hooks/UserContext";
import { WindowLocationProvider } from "hooks/WindowLocationContext";
import { formatJobsResponse } from "orm/jobs";
import {
    fetchApiCompanySuggestions, fetchApiLocationSuggestions,
    fetchJobs, formatCompanySuggestions, fetchCompanyAttributes
} from "../utils/api";
import {
    jobCategoryItems,
    postedDateRangeItems,
    searchRadiusItems,
    convertToFacetItemsFormat,
    flattenPerksAndBenefits
} from "../utils/searchFacetItems";
import { getFacetValuesFromFacetItems } from "../utils/searchFacets";
import { FeatureFlagsProvider } from "../hooks/FeatureFlagsContext";
import { SearchFacetsProvider } from "../hooks/SearchFacetsContext";
import { SearchProvider } from "../hooks/SearchContext";
import { SearchResultsProvider } from "../hooks/SearchResultsContext";
import { Seo } from "../components/header/Seo";
import { SearchPageLayout } from "../components/content/SearchPageLayout";
import {
    generateRandomString,
    IMetadataObj,
    titleCase,
} from "../utils/strings";
import { getActiveFeatureFlags } from "../utils/featureFlags";

// Expire once a day, check every 5 minutes
const apiCache = new NodeCache({ stdTTL: 86400, checkperiod: 300 });

/**
 * A config for the experiments.
 * @example
 * {
 *     name: "EXPERIMENT_KEY",
 *     variants: [{
 *         name: "control",
 *         weight: 50
 *     }, {
 *         name: "enabled",
 *         weight: 50
 *     }]
 * }
 */
const experimentConfig: Experiment[] = [];

const getInitialLocationSuggestions = async (
    query: ParsedUrlQuery
): Promise<{ [key: string]: { [key: string]: string } }> => {
    const userInput = query?.location as string;
    const locationSuggestions = await fetchApiLocationSuggestions(userInput);
    if (locationSuggestions.length) {
        return {
            location: locationSuggestions[0]
        };
    }
    return {};
};

const getInitialCompanySuggestions = async (companySearchString: string): Promise<{[key: string]: any}> => {
    const { data } = await fetchApiCompanySuggestions(companySearchString as string);
    const suggestedCompany = data.find((company) => company.value.short_name === companySearchString);
    if (suggestedCompany) {
        const { text, value } = suggestedCompany;

        return {
            company: {
                name: text,
                shortname: value.short_name
            }
        };
    }
    // Get a single job as a way of
    // making a job request by shortname.
    // We don't use CTS here because it's
    // expensive (money-wise).
    const { hits } = await fetchJobs({ company_short_name: companySearchString, limit: 1 }, false);

    if (hits.length) {
        const { company } = hits[0].hit;

        return {
            company: {
                name: company.name,
                shortname: company.short_name
            }
        };
    }

    // The assumption is that context.query.company is the shortname
    return {
        company: {
            name: companySearchString,
            shortname: companySearchString
        }
    };
};

export const getInitialFilterParams = (params: IMetadataObj) => Object.keys(params)
    .reduce((values, currentValue): IMetadataObj => {
        const localValues = values;

        if (!["location", "company"].includes(currentValue)) {
            const decodedParamValue = decodeURIComponent(params[currentValue]);
            // Format keyword
            let value = decodedParamValue;
            if (currentValue === "keyword") {
                value = titleCase(value.replace(/\+/g, " "));
            }
            localValues[currentValue] = value;
        }

        return localValues;
    }, {} as IMetadataObj);

export const getInitialSearchId = (reqUrl: string, reqHost: string) => {
    const parsedUrl = new URL(reqUrl || "", `http://${reqHost || "www.example.com"}`);
    const searchIdValue = parsedUrl.searchParams.get("_search_id");
    return searchIdValue || uuidv4();
};

const getCompanyAttributes = async () => {
    const cachedCompanyAttributes = apiCache.get("companyAttributes");
    const { publicRuntimeConfig: { appName, appEnv } } = getConfig();
    if (cachedCompanyAttributes === undefined) {
        const apiCompanyAttributes = await fetchCompanyAttributes();
        apiCache.set("companyAttributes", apiCompanyAttributes);

        if (appEnv !== "production") {
            console.info(`${appName} retrieved company attributes from API. Cache is expired.`);
        }

        return apiCompanyAttributes;
    }

    if (appEnv !== "production") {
        console.info(`${appName} retrieved company attributes from cache.  Cache is fresh.`);
    }

    return cachedCompanyAttributes;
};

const isValidJSON = (blob: string) => {
    try {
        JSON.parse(blob);
        return true;
    } catch (error) {
        const msg = `Search-Renderer could not parse the carmen cookie. ${blob} is not valid json.`;
        console.error(msg, JSON.stringify(formatLog({ error })));
        return false;
    }
};

const getCarmenDataFromContext = (context: GetServerSidePropsContext) => {
    const { req } = context;
    const getRegex = /carmen_location=(\{.*\})/;
    const setCookieHeader = context.res.getHeader("set-cookie");
    const defaultCarmenData = {
        city: "",
        region: "",
        country: "",
        dma: "",
        lat: null,
        lon: null,
        accuracy_radius: null
    };

    if ("carmen_location" in req.cookies && isValidJSON(req.cookies?.carmen_location ?? null)) {
        return JSON.parse(req.cookies.carmen_location);
    }

    if (setCookieHeader) {
        // Cookies set in middleware in NextJS are not usable
        // on th request in which they were set. We check the
        // set-cookie header in this case.
        const cookieData = `${setCookieHeader}`.match(getRegex)?.[1] ?? null;
        if (cookieData && isValidJSON(cookieData)) {
            return JSON.parse(cookieData);
        }
    }
    return defaultCarmenData;
};

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const { query, req: { headers: { cookie } } } = context;
    const { publicRuntimeConfig: { appName } } = getConfig();
    const experiments = getExperiments(experimentConfig, context);
    const ctsEnabled = query?.ctsSearch === "enabled";
    let initialDropDownCompanies: SearchDropdownProps["dropdownProps"]["dropdownList"] = [];
    let initialSearchParams = {};
    const carmenLocation = getCarmenDataFromContext(context);
    const preference = generateRandomString();

    try {
        const response = await fetchApiCompanySuggestions("a");
        initialDropDownCompanies = formatCompanySuggestions(response.data);
    } catch (error) {
        const msg = `${appName} was unable to fetch companies.`;
        console.error(msg, JSON.stringify(formatLog({ context, error })));
        throw new Error(msg);
    }

    if (query.location) {
        try {
            const initialLocationParams = await getInitialLocationSuggestions(query);
            initialSearchParams = {
                ...context.query,
                ...initialLocationParams
            };
        } catch (error) {
            const msg = `${appName} was unable to fetch location: ${query?.location}`;
            console.error(msg, JSON.stringify(formatLog({ context, error })));
            throw new Error(msg);
        }
    }

    if (query.company) {
        try {
            const initialCompanyParams = await getInitialCompanySuggestions(query?.company as string);
            initialSearchParams = {
                ...initialSearchParams,
                ...initialCompanyParams
            };
        } catch (error) {
            const msg = `${appName} was unable to fetch company: ${query?.company}`;
            console.error(msg, JSON.stringify(formatLog({ context, error })));
            throw new Error(msg);
        }
    }

    const companyAttributes = await getCompanyAttributes();

    initialSearchParams = {
        ...initialSearchParams,
        ...getInitialFilterParams(query as IMetadataObj),
        preference,
        searchId: getInitialSearchId(context.req?.url, context.req.headers?.host)
    };

    const initialSearchState = setInitialSearchState(
        initialSearchParams,
        companyAttributes,
        initialDropDownCompanies,
        carmenLocation,
        ctsEnabled
    );

    const {
        filters,
        latlng,
        remoteOption,
        companyShortName,
        searchKeyword,
    } = searchResultsSelector(initialSearchState, carmenLocation);

    const queryParams = getParams({
        filters,
        companyShortName,
        searchKeyword,
        remoteOption,
        latlng,
        searchApiPreference: preference
    });

    let initialSearchResultsState = null;

    const jobsResponse = await fetchJobs(queryParams, ctsEnabled, cookie);
    initialSearchResultsState = formatJobsResponse(jobsResponse);

    return {
        props: {
            companyAttributes: companyAttributes || {
                industries: [],
                leadership_attributes: [],
                perks_and_benefits: {}
            },
            initialSearchState,
            initialSearchResultsState,
            carmenLocation,
            experiments,
            featureFlags: {
                ...getActiveFeatureFlags(context)
            }
        }
    };
};

export const setInitialSearchState = (
    initialSearchParams,
    companyAttributes,
    initialDropDownCompanies,
    carmenLocation,
    ctsEnabled
) => {
    const {
        location = null,
        company = null,
        keyword = "",
        category = "",
        industry = "",
        level = "",
        perks = "",
        size = "",
        radius = "",
        "date-posted": postedDateRange = "",
        "company-leadership": companyLeadership = "",
        preference = "",
        searchId = ""
    } = initialSearchParams;
    const { diversity_and_inclusion_attributes, industries, perks_and_benefits } = companyAttributes;

    let foundSeniorLevel = false;
    const experienceLevel: string[] = level.split(",").map((levelValue: string) => {
        if (["senior", "management"].includes(levelValue)) {
            if (foundSeniorLevel) {
                return "";
            }
            foundSeniorLevel = true;
            return "senior,management";
        }
        return levelValue;
    }).filter((levelValue: string) => !!levelValue);

    const radiusValues = getFacetValuesFromFacetItems(radius, searchRadiusItems);
    const initialRadius = location ? radiusValues[0] ?? "" : "";

    const postedDateRangeValues = getFacetValuesFromFacetItems(postedDateRange, postedDateRangeItems);
    const initialPostedDateRange = postedDateRangeValues[0] ?? "";

    return {
        location: location ? {
            userInputValue: location.label,
            userSelectedLocation: {
                label: location.label === "Flexible / Remote" ? "Remote / Flexible" : location.label,
                latitude: location.latitude,
                longitude: location.longitude,
                remote_work_location_type: location.label === "Remote / Flexible"
                    || location.label === "Flexible / Remote" ? "virtual" : "",
                value: location.value === "Flexible / Remote" ? "Remote / Flexible" : location.value,
            }
        } : {
            userInputValue: "",
            userSelectedLocation: {
                label: "",
                latitude: company ? null : carmenLocation?.lat || null,
                longitude: company ? null : carmenLocation?.lon || null,
                value: ""
            }
        },
        company: company ? {
            userSelectedCompany: {
                label: company.name,
                value: company.shortname
            },
            companySuggestions: initialDropDownCompanies
        } : {},
        keyword: {
            userInputValue: keyword || "",
            userSelectedKeyword: {
                label: keyword || "",
                value: keyword || ""
            }
        },
        filters: {
            experienceLevel,
            category: getFacetValuesFromFacetItems(
                category, jobCategoryItems
            ),
            industry: getFacetValuesFromFacetItems(industry, convertToFacetItemsFormat(industries)),
            perks: getFacetValuesFromFacetItems(
                perks,
                convertToFacetItemsFormat(flattenPerksAndBenefits(perks_and_benefits))
            ),
            companyLeadership: getFacetValuesFromFacetItems(
                companyLeadership,
                convertToFacetItemsFormat(diversity_and_inclusion_attributes)
            ),
            companySize: size?.length ? decodeURIComponent(size).split(",") : [],
            radius: initialRadius,
            postedDateRange: initialPostedDateRange
        },
        preference,
        searchId,
        ctsEnabled
    };
};

const SnowplowInit = () => {
    const user = useUserState();
    const { publicRuntimeConfig } = getConfig();
    const [pageViewTriggered, setPageViewTriggered] = useState(false);

    useEffect(() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(
            {
                sp_page_is_external: false,
                sp_page_section: "search",
                sp_page_sponsor_id: null,
                sp_page_tab: null,
                sp_page_type: "collection",
                sp_experiment_experiment_id: "",
                sp_experiment_variant_id: ""
            }
        );
        if (publicRuntimeConfig?.snowplowCollector) {
            initSnowplowTracker(publicRuntimeConfig.snowplowCollector);
        }
    }, []);

    React.useEffect(() => {
        window.sp_user_logged_in = user.logged_in ?? false;
        window.sp_user_id = user.id ?? null;
    }, [user]);

    // Note: Add snowplow pageview in a useEffect??
    // After a user api request attempt has been made??
    React.useEffect(() => {
        if (user.isInitialized && !pageViewTriggered) {
            triggerSnowplowPageViewEvent();
            setPageViewTriggered(true);
        }
    }, [user]);

    return null;
};

const OskarUserInit = () => {
    const user = useUserState();

    React.useEffect(() => {
        // NOTE: An email with `themuse.com` does not mean the user is an admin
        const isMuser = user?.email && user.email.split("@")[1] === "themuse.com";
        userTrack({
            signed_in: user.logged_in ?? false,
            is_muser: isMuser,
            is_admin: isMuser,
            user_id: user.id ?? null
        });
    }, [user]);

    return null;
};

const Home = (props: InferGetServerSidePropsType<typeof getServerSideProps>) => {
    const {
        initialSearchState,
        initialSearchResultsState,
        experiments,
        carmenLocation,
        companyAttributes,
        featureFlags
    } = props;

    const router = useRouter();
    const { asPath } = router;

    return (
        <ExperimentProvider experiments={experiments}>
            <UserProvider userLocation={carmenLocation}>
                <FeatureFlagsProvider featureFlags={featureFlags}>
                    <SearchFacetsProvider companyFacets={companyAttributes}>
                        <SearchProvider {...initialSearchState}>
                            <WindowLocationProvider>
                                <SearchResultsProvider {...initialSearchResultsState}>
                                    <SnowplowInit />
                                    <OskarUserInit />
                                    <Seo {...{ searchUrl: asPath }} />
                                    <SearchPageLayout />
                                </SearchResultsProvider>
                            </WindowLocationProvider>
                        </SearchProvider>
                    </SearchFacetsProvider>
                </FeatureFlagsProvider>
            </UserProvider>
        </ExperimentProvider>
    );
};

export default Home;
